import { useState, useEffect, useCallback } from 'react';
import { HelpCircle, X, ChevronRight, ChevronLeft, Lightbulb } from 'lucide-react';
import { Button } from './button';

const WalkthroughOverlay = ({ steps = [], onClose }) => {
  const [current, setCurrent] = useState(0);
  const [spotlightRect, setSpotlightRect] = useState(null);

  const step = steps[current];

  const updateSpotlight = useCallback(() => {
    if (!step?.target) { setSpotlightRect(null); return; }
    const el = document.querySelector(step.target);
    if (el) {
      const rect = el.getBoundingClientRect();
      setSpotlightRect({ top: rect.top - 8, left: rect.left - 8, width: rect.width + 16, height: rect.height + 16 });
      el.scrollIntoView({ behavior: 'smooth', block: 'center' });
    } else {
      setSpotlightRect(null);
    }
  }, [step]);

  useEffect(() => {
    updateSpotlight();
    const t = setTimeout(updateSpotlight, 300);
    return () => clearTimeout(t);
  }, [current, updateSpotlight]);

  useEffect(() => {
    const onKey = (e) => {
      if (e.key === 'Escape') onClose();
      if (e.key === 'ArrowRight' && current < steps.length - 1) setCurrent(c => c + 1);
      if (e.key === 'ArrowLeft' && current > 0) setCurrent(c => c - 1);
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [current, steps.length, onClose]);

  if (!steps.length) return null;

  // Calculate tooltip position
  const getTooltipStyle = () => {
    if (!spotlightRect) return { top: '50%', left: '50%', transform: 'translate(-50%, -50%)' };
    const vw = window.innerWidth;
    const vh = window.innerHeight;
    const centerX = spotlightRect.left + spotlightRect.width / 2;
    const centerY = spotlightRect.top + spotlightRect.height / 2;

    // Place below if there's room, else above
    if (spotlightRect.top + spotlightRect.height + 220 < vh) {
      return { top: spotlightRect.top + spotlightRect.height + 16, left: Math.max(20, Math.min(centerX - 180, vw - 400)) };
    }
    return { top: Math.max(20, spotlightRect.top - 200), left: Math.max(20, Math.min(centerX - 180, vw - 400)) };
  };

  return (
    <div className="fixed inset-0 z-[9999]" data-testid="walkthrough-overlay">
      {/* Dark overlay with spotlight cutout */}
      <svg className="absolute inset-0 w-full h-full" style={{ pointerEvents: 'none' }}>
        <defs>
          <mask id="spotlight-mask">
            <rect x="0" y="0" width="100%" height="100%" fill="white" />
            {spotlightRect && (
              <rect
                x={spotlightRect.left} y={spotlightRect.top}
                width={spotlightRect.width} height={spotlightRect.height}
                rx="12" fill="black"
              />
            )}
          </mask>
        </defs>
        <rect x="0" y="0" width="100%" height="100%" fill="rgba(0,0,0,0.7)" mask="url(#spotlight-mask)" />
      </svg>

      {/* Spotlight ring */}
      {spotlightRect && (
        <div
          className="absolute border-2 border-neutral-400 rounded-xl pointer-events-none"
          style={{ top: spotlightRect.top, left: spotlightRect.left, width: spotlightRect.width, height: spotlightRect.height, boxShadow: '0 0 0 4px rgba(0,0,0,0.08), 0 0 30px rgba(0,0,0,0.08)' }}
        />
      )}

      {/* Click-through zone on overlay */}
      <div className="absolute inset-0" onClick={onClose} style={{ cursor: 'default' }} />

      {/* Tooltip card */}
      <div
        className="absolute z-10 w-[360px] bg-white rounded-2xl shadow-2xl border border-neutral-200 overflow-hidden"
        style={getTooltipStyle()}
        onClick={e => e.stopPropagation()}
        data-testid="walkthrough-tooltip"
      >
        {/* Progress bar */}
        <div className="h-1 bg-neutral-100">
          <div className="h-full bg-black transition-all duration-300" style={{ width: `${((current + 1) / steps.length) * 100}%` }} />
        </div>

        <div className="p-5">
          <div className="flex items-start justify-between mb-3">
            <div className="flex items-center gap-2">
              <div className="h-7 w-7 rounded-lg bg-neutral-100 flex items-center justify-center">
                <Lightbulb className="h-4 w-4 text-neutral-600" strokeWidth={1.5} />
              </div>
              <span className="text-[10px] font-bold uppercase tracking-wider text-neutral-500">Stap {current + 1} van {steps.length}</span>
            </div>
            <button onClick={onClose} className="p-1.5 hover:bg-neutral-100 rounded-lg transition-colors" data-testid="walkthrough-close">
              <X className="h-4 w-4 text-neutral-400" strokeWidth={1.5} />
            </button>
          </div>

          <h3 className="text-sm font-bold text-black mb-2">{step.title}</h3>
          <p className="text-xs text-neutral-600 leading-relaxed">{step.description}</p>

          {step.tip && (
            <div className="mt-3 p-3 bg-neutral-50 border border-neutral-200 rounded-lg">
              <p className="text-[10px] text-neutral-700"><span className="font-bold">Tip:</span> {step.tip}</p>
            </div>
          )}
        </div>

        <div className="flex items-center justify-between px-5 pb-4">
          <Button
            variant="ghost" size="sm"
            onClick={() => setCurrent(c => Math.max(0, c - 1))}
            disabled={current === 0}
            className="text-xs h-8 gap-1"
            data-testid="walkthrough-prev"
          >
            <ChevronLeft className="h-3.5 w-3.5" strokeWidth={1.5} />Vorige
          </Button>

          <div className="flex gap-1">
            {steps.map((_, i) => (
              <button key={i} onClick={() => setCurrent(i)}
                className={`h-1.5 rounded-full transition-all ${i === current ? 'w-4 bg-black' : 'w-1.5 bg-neutral-200 hover:bg-neutral-300'}`}
              />
            ))}
          </div>

          {current < steps.length - 1 ? (
            <Button
              size="sm"
              onClick={() => setCurrent(c => c + 1)}
              className="text-xs h-8 gap-1 bg-black text-white hover:bg-neutral-800"
              data-testid="walkthrough-next"
            >
              Volgende<ChevronRight className="h-3.5 w-3.5" strokeWidth={1.5} />
            </Button>
          ) : (
            <Button
              size="sm"
              onClick={onClose}
              className="text-xs h-8 bg-black text-white hover:bg-neutral-800"
              data-testid="walkthrough-finish"
            >
              Begrepen
            </Button>
          )}
        </div>
      </div>
    </div>
  );
};

export const HelpButton = ({ steps = [] }) => {
  const [open, setOpen] = useState(false);

  if (!steps.length) return null;

  return (
    <>
      <Button
        variant="outline"
        size="sm"
        onClick={() => setOpen(!open)}
        className={`border-neutral-200 gap-1.5 ${open ? 'bg-neutral-100 border-neutral-300 text-black' : ''}`}
        data-testid="help-button"
      >
        <HelpCircle className="h-3.5 w-3.5" strokeWidth={1.5} />
        Hulp
      </Button>
      {open && <WalkthroughOverlay steps={steps} onClose={() => setOpen(false)} />}
    </>
  );
};
